import { useState } from 'react';

import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { useForms } from '../data/forms.js';
import type { ChannelId, Form, InboxItem } from '../fixtures/domain.js';
import { plural } from '../util/format.js';
import { useDismiss } from '../util/useDismiss.js';
import { useUnloadGuard } from '../util/useUnloadGuard.js';
import { FormList } from '../form/FormList.js';
import { BULK_MAX, type BulkResult } from './bulkSend.js';
import { ListPicker } from './ListPicker.js';
import { ScopeBox } from './ScopeBox.js';
import styles from './BulkSendDialog.module.css';

interface BulkSendDialogProps {
  /** The conversations the message goes to — also the set the "Add to list" in
   *  the scope box files, so a bulk send can file the same chats in one breath. */
  targets: InboxItem[];
  /** Runs the send; resolves with the per-channel outcome. `form` is the one the
   *  employer attached, or null for a plain message. `onProgress` reports how
   *  many have landed, for the bar over the composer. */
  onSend: (
    text: string,
    form: Form | null,
    onProgress: (done: number, total: number) => void,
  ) => Promise<BulkResult<ChannelId>>;
  /** Close the dialog. `sent` is true once a send has run, so the caller clears
   *  the selection then but leaves it intact on a plain cancel. */
  onClose: (sent: boolean) => void;
}

/**
 * Which screen of the dialog is up.
 *
 * Three, not two: attaching a form is a choice with its own room, for the
 * reason the thread's picker is a screen rather than a menu — two screening
 * forms differ in their options long before their titles, so choosing one means
 * reading one. It is a step of this dialog rather than a dialog over it: a
 * second scrim over the first, each with its own Escape, is two ways out of one
 * decision.
 */
type Step = 'scope' | 'compose' | 'form';

/**
 * Compose one message and send it to every selected conversation, in the order
 * the decisions are actually made: who first — the faces it reaches, with "Add
 * to list" in the box foot — then what, a composer that frosts under a progress
 * bar while the batch runs. Mirrors the design mock's bulk-message flow.
 *
 * Behind the composer sits a third screen for choosing the form the message
 * carries. One message either way: the words the employer writes are the form
 * card's own note, the way they are in the thread's picker, so a form and a
 * message are never two things arriving in a chat one after the other.
 */
export function BulkSendDialog({ targets, onSend, onClose }: BulkSendDialogProps) {
  const count = targets.length;
  const channelIds = targets.map((item) => item.channel.id);
  const [text, setText] = useState('');
  const [step, setStep] = useState<Step>('scope');
  /** The form the message carries, once one has been attached. */
  const [form, setForm] = useState<Form | null>(null);
  /** What the attach step is showing. Not attached until Attach is pressed. */
  const [pick, setPick] = useState<string | null>(null);
  const { forms, loading: formsLoading, error: formsError } = useForms();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<BulkResult<ChannelId> | null>(null);
  const sending = progress !== null;
  // The run is a loop in this page: leaving takes the rest of it with them.
  useUnloadGuard(sending);

  // `sent` gates whether the caller clears the selection: only once something
  // actually landed. A total failure keeps the selection so a retry is one tap.
  const close = () => onClose(result !== null && result.done > 0);

  // Never yank the dialog mid-send: a half-run that vanishes leaves the employer
  // unsure what actually went out.
  useDismiss(true, () => {
    if (!sending) close();
  });

  // Over the ceiling, block the send rather than silently dropping the overflow —
  // runBulk's slice would otherwise leave those conversations in neither count.
  const overCap = count > BULK_MAX;
  const written = text.trim() !== '';
  /**
   * What there is to send. A message on its own, or a form on its own: with a
   * form attached the words are optional, because the card carries the form's
   * own name and the inbox preview falls back to it — which is what the
   * thread's picker has always allowed of its note.
   */
  const ready = written || form !== null;
  // `count > 0` because it can drop under us: a revision re-read while the compose
  // step is open can drop every picked row off the filtered list, and an empty
  // send would fire analytics for nothing and divide by zero on the bar.
  const canSend = count > 0 && ready && !sending && !overCap;

  const run = async () => {
    if (!canSend) return;
    setProgress({ done: 0, total: count });
    try {
      setResult(await onSend(text.trim(), form, (done, total) => setProgress({ done, total })));
    } finally {
      setProgress(null);
    }
  };

  /**
   * The form the message will carry, along the composer's foot and inside its
   * box.
   *
   * Inside, because that is what says the form and the words go out together —
   * the same reason a staged file rides in the thread composer's own outline
   * rather than under it. It reads back what it is holding, which makes the row
   * the way in and the receipt at once: one place to look for "is a form going
   * with this", rather than a button that stays the same however many times it
   * is pressed.
   */
  const strip = (
    <div className={styles.attach}>
      <button
        type="button"
        className={styles.attachMain}
        onClick={() => {
          // On the form already attached, and otherwise on nothing: the step
          // opens as a list of names, which is the question it is asking. A
          // first row selected on arrival would be a form nobody chose, one
          // press from going to every chat in the set — and its questions,
          // open, would push the rest of the library under the fold.
          setPick(form?.id ?? null);
          setStep('form');
        }}
        disabled={sending}
        title={form ? 'Change form' : undefined}
      >
        <Icon name="listChecks" size={16} />
        <span className={styles.attachLabel}>{form ? form.name : 'Attach form'}</span>
        {/* The count where a form is held, the chevron where none is: one slot,
            and it says either what is in it or that there is somewhere to go. */}
        {form ? (
          <span className={styles.attachMeta}>{plural(form.fields.length, 'question')}</span>
        ) : (
          <Icon name="chevronRight" size={15} />
        )}
      </button>
      {form && (
        <button
          type="button"
          className={styles.attachRemove}
          onClick={() => setForm(null)}
          disabled={sending}
          aria-label={`Remove ${form.name}`}
          title="Remove"
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );

  return (
    <div className={styles.overlay} onMouseDown={() => !sending && close()}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {result ? (
          <>
            <div className={styles.headerPlain}>
              <h2 className={styles.title}>
                Sent to {result.done} of {result.done + result.failed.length}
              </h2>
              {result.failed.length > 0 && (
                <p className={styles.failed}>
                  {result.failed.length} couldn't be sent — try those again from their own threads.
                </p>
              )}
            </div>
            <div className={styles.footer}>
              <Button variant="primary" onClick={close}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            {step === 'form' ? (
              <div className={styles.header}>
                <span className={styles.iconBadge}>
                  <Icon name="listChecks" size={19} />
                </span>
                <div className={styles.heading}>
                  <h2 className={styles.title}>Attach a form</h2>
                  {/* What the employer cannot see from here: that this is not a
                      second message. The words in the composer are the card's
                      own note, and both go out together. */}
                  <p className={styles.subtitle}>Sent with the message, as a card.</p>
                </div>
              </div>
            ) : step === 'compose' ? (
              <div className={styles.header}>
                <span className={styles.iconBadge}>
                  <Icon name="pencil" size={19} filled={false} />
                </span>
                <div className={styles.heading}>
                  <h2 className={styles.title}>Write the message</h2>
                  <p className={styles.subtitle}>Goes to {plural(count, 'conversation')}.</p>
                </div>
              </div>
            ) : (
              <div className={styles.headerPlain}>
                <h2 className={styles.title}>Sending to {plural(count, 'conversation')}</h2>
                <p className={styles.subtitle}>Everyone gets the same message.</p>
              </div>
            )}

            <div className={styles.body}>
              {step === 'scope' && (
                <>
                  {/* Add to list rides in the box's foot — file the same chats the
                      message goes to without a second trip. */}
                  <ScopeBox targets={targets} foot={<ListPicker channelIds={channelIds} />} />
                  {overCap && (
                    <p className={styles.overCap}>
                      You can message up to {BULK_MAX} at once — deselect {count - BULK_MAX} to
                      send.
                    </p>
                  )}
                </>
              )}

              {step === 'form' && (
                <div className={styles.chooser}>
                  {formsError ? (
                    /* An outage is not an empty library, and the two states
                       offer opposite next moves: one says go and write a form,
                       the other says the library is fine and this trip is not. */
                    <EmptyState
                      icon={<Icon name="listChecks" size={32} />}
                      title="Couldn't load your forms"
                      description="Close this and try again in a moment."
                    />
                  ) : formsLoading ? null : forms.length === 0 ? (
                    /* No way out to Forms from in here, and that is the point:
                       the trip would take the picked chats and the written
                       message with it, since the section that holds the library
                       is not the section this dialog belongs to. So the state
                       says where forms are made and leaves the dialog standing. */
                    <EmptyState
                      icon={<Icon name="listChecks" size={32} />}
                      title="No forms yet"
                      description="Create one in Forms, then attach it here."
                    />
                  ) : (
                    <FormList forms={forms} selectedId={pick} onSelect={setPick} />
                  )}
                </div>
              )}

              {/* Kept mounted across Back so an edited draft is not thrown away
                  (the editor owns it in its own DOM); hidden, not unmounted, on
                  the other steps. `autoFocus` flips with the step to seat the
                  caret on arrival. */}
              <div className={step === 'compose' ? styles.composeSlot : styles.stowed}>
                <RichTextEditor
                  onChange={setText}
                  disabled={sending}
                  autoFocus={step === 'compose'}
                  placeholder="Type your message…"
                  foot={strip}
                />
                {progress && (
                  <div className={styles.run}>
                    <div className={styles.runTrack}>
                      <div
                        className={styles.runBar}
                        style={{ width: `${(progress.done / progress.total) * 100}%` }}
                      />
                    </div>
                    <p className={styles.runLabel}>
                      Sending to {progress.done}/{progress.total}
                    </p>
                  </div>
                )}
              </div>
              {count === 0 && (
                <p className={styles.overCap}>
                  These conversations left your inbox — nothing to send. Close and start again.
                </p>
              )}
            </div>

            <div className={styles.footer}>
              {step === 'form' ? (
                <>
                  <Button variant="ghost" onClick={() => setStep('compose')}>
                    Back
                  </Button>
                  {/* Attaching is not sending, so this screen confirms nothing:
                      it hands the form back to the composer, where the message
                      it rides on still has to be sent.

                      Gone rather than greyed on an empty library: a disabled
                      button beside a state that has just said there is nothing
                      here is the same sentence twice, once in a shape that
                      invites a press. */}
                  {forms.length > 0 && (
                    <Button
                      variant="primary"
                      disabled={pick === null}
                      onClick={() => {
                        setForm(forms.find((entry) => entry.id === pick) ?? null);
                        setStep('compose');
                      }}
                    >
                      Attach
                    </Button>
                  )}
                </>
              ) : step === 'compose' ? (
                <>
                  <Button variant="ghost" onClick={() => setStep('scope')} disabled={sending}>
                    Back
                  </Button>
                  <Button variant="primary" onClick={run} disabled={!canSend}>
                    {sending ? 'Sending…' : `Send to ${count}`}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setStep('compose')}
                    disabled={overCap || count === 0}
                  >
                    Next
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
